import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { KeyRound, Plus, Monitor, Key } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const tipoLabels = {
    fisico: 'Físico',
    logico: 'Lógico'
};

const AccessLogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { hasRole, user } = useAuth();

    const [formData, setFormData] = useState({
        tipo: 'fisico',
        local: '',
        motivo: '',
        responsavel_nome: ''
    });

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData(prev => ({ ...prev, responsavel_nome: user.nome }));
        }
    }, [user]);

    const fetchLogs = async () => {
        try {
            const response = await axios.get(`${API}/access-logs`);
            setLogs(response.data);
        } catch (error) {
            toast.error('Erro ao carregar registros');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            await axios.post(`${API}/access-logs`, formData);
            toast.success('Acesso registrado!');
            setDialogOpen(false);
            resetForm();
            fetchLogs();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar');
        }
    };

    const resetForm = () => {
        setFormData({
            tipo: 'fisico',
            local: '',
            motivo: '',
            responsavel_nome: user?.nome || ''
        });
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="access-logs-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Controle de Acesso
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Registro de acessos físicos e lógicos
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-access"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Registrar Acesso
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Registrar Acesso
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Tipo de Acesso
                                    </Label>
                                    <Select 
                                        value={formData.tipo} 
                                        onValueChange={(value) => setFormData({...formData, tipo: value})}
                                    >
                                        <SelectTrigger data-testid="select-access-tipo" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            {Object.entries(tipoLabels).map(([key, label]) => (
                                                <SelectItem key={key} value={key}>{label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Local / Sistema
                                    </Label>
                                    <Input
                                        data-testid="input-access-local"
                                        value={formData.local}
                                        onChange={(e) => setFormData({...formData, local: e.target.value})}
                                        required
                                        placeholder="Ex: Sala de Monitoramento, Sistema Radar..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Motivo
                                    </Label>
                                    <Input
                                        data-testid="input-access-motivo"
                                        value={formData.motivo}
                                        onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                                        required
                                        placeholder="Motivo do acesso..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-access"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Registrar
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {logs.length === 0 ? (
                        <div className="p-12 text-center">
                            <KeyRound className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum registro de acesso</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Local / Sistema</TableHead>
                                    <TableHead>Motivo</TableHead>
                                    <TableHead>Responsável</TableHead>
                                    <TableHead>Data/Hora</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id} data-testid={`access-row-${log.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {log.tipo === 'fisico' ? (
                                                    <Key className="w-4 h-4 text-amber-400" />
                                                ) : (
                                                    <Monitor className="w-4 h-4 text-cyan-400" />
                                                )}
                                                <span className="text-slate-300">{tipoLabels[log.tipo]}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {log.local}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {log.motivo}
                                        </TableCell>
                                        <TableCell className="text-slate-300">
                                            {log.responsavel_nome}
                                        </TableCell>
                                        <TableCell className="text-slate-500 text-xs font-mono">
                                            {formatDate(log.data_hora)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AccessLogsPage;
